import clsx from 'clsx';
import type {MutableRefObject} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Controller, useFormContext} from 'react-hook-form';
import {useValueLists, CountryListItem} from '@common/http/value-lists';
import {useFloatingPosition} from '@common/ui/overlays/floating-position';
import {rootEl} from '@common/core/root-el';
import {message} from '@common/i18n/message';
import {useTrans} from '@common/i18n/use-trans';
import {authFieldHalfClass, authActionOutsetClass} from './auth-tokens';

const PANEL_WIDTH = 250;
const PANEL_HEIGHT = 437;
const ROW_WIDTH = 188;
const ROW_HEIGHT = 60;
const ROW_GAP = 8;
/** Vertical padding inside panel (~32px top/bottom) */
const PANEL_PAD_Y = 32;
/** Left inset to center 188px rows: (250 - 188) / 2 */
const PANEL_PAD_LEFT = 31;
/** Scrollbar gutter: 7.5px space + 9px thumb */
const PANEL_PAD_RIGHT = 16.5;

const countryInputClass = clsx(
  authFieldHalfClass,
  'w-full !border-2 !border-transparent',
  'focus:!ring-0 focus:!border-[#2B7E20]'
);

const countryRowClass = clsx(
  'box-border flex w-[188px] shrink-0 items-center',
  'rounded-[24px] border-2 border-[#E9ECF0] bg-white dark:border-[#2a2d33] dark:bg-[#232832]',
  'px-[20px] text-left text-[16px] font-normal leading-normal text-[#535353] dark:text-[#f3f4f6]',
  'transition-colors hover:border-[#2B7E20] focus:border-[#2B7E20] focus:outline-none'
);

function CountryPanelCloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden
    >
      <circle cx="20" cy="20" r="20" fill="#D8562A" />
      <path
        d="M14.166 14.1665L25.8327 25.8332"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M14.166 25.8335L25.8327 14.1668"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface AuthCountrySelectProps {
  name: string;
  className?: string;
  required?: boolean;
}

/** Custom country picker for register (no generic listbox shell). */
export function AuthCountrySelect({
  name,
  className = 'w-[163px] shrink-0',
  required = true,
}: AuthCountrySelectProps) {
  const {trans} = useTrans();
  const {control} = useFormContext();
  const {data, isLoading} = useValueLists(['countries']);
  const countries = useMemo(
    () =>
      [...(data?.countries ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data?.countries]
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const {x, y, strategy, reference, floating} = useFloatingPosition({
    ref: inputRef,
    placement: 'top-start',
    offset: 6,
    fixedFloatingWidth: PANEL_WIDTH,
    maxHeight: PANEL_HEIGHT,
    shiftCrossAxis: false,
  });

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        inputRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  const filterCountries = (list: CountryListItem[], q: string) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(c => c.name.toLowerCase().includes(needle));
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={
        required
          ? {required: trans(message('Please select a country'))}
          : undefined
      }
      render={({field, fieldState}) => {
        const selected = countries.find(c => c.code === field.value);
        const filtered = filterCountries(countries, isOpen ? query : '');
        const inputValue = isOpen ? query : selected?.name ?? '';

        const openPanel = () => {
          setQuery(selected?.name ?? '');
          setIsOpen(true);
        };

        const selectCountry = (country: CountryListItem) => {
          field.onChange(country.code);
          setQuery(country.name);
          setIsOpen(false);
          inputRef.current?.blur();
        };

        const closePanel = () => {
          setIsOpen(false);
          setQuery(selected?.name ?? '');
        };

        return (
          <div className={clsx('relative', className)}>
            <input
              ref={mergeInputRef(reference, inputRef)}
              type="text"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder={trans(message('Country'))}
              aria-label={trans(message('Country'))}
              value={inputValue}
              aria-expanded={isOpen ? 'true' : 'false'}
              aria-haspopup="listbox"
              aria-invalid={fieldState.invalid ? 'true' : undefined}
              className={clsx(countryInputClass, isOpen && '!border-[#2B7E20]')}
              onFocus={openPanel}
              onClick={openPanel}
              onChange={e => {
                setQuery(e.target.value);
                setIsOpen(true);
                if (!e.target.value.trim()) {
                  field.onChange(null);
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  closePanel();
                }
              }}
            />

            {fieldState.error?.message ? (
              <p className="mt-4 text-center text-xs text-red-600">
                {fieldState.error.message}
              </p>
            ) : null}

            {isOpen &&
              rootEl &&
              createPortal(
                <div
                  ref={node => {
                    panelRef.current = node;
                    floating(node);
                  }}
                  className="relative z-[200]"
                  style={{
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0,
                    width: PANEL_WIDTH,
                    height: PANEL_HEIGHT,
                  }}
                >
                  <button
                    type="button"
                    onClick={closePanel}
                    className={clsx(
                      authActionOutsetClass,
                      'z-10 flex h-[40px] w-[40px] items-center justify-center transition-opacity hover:opacity-90'
                    )}
                    aria-label={trans(message('Close'))}
                  >
                    <CountryPanelCloseIcon />
                  </button>

                  <div
                    className={clsx(
                      'flex h-full w-full flex-col overflow-hidden',
                      'rounded-[30px] border-2 border-[#E9ECF0] bg-[#F8F9FF] dark:border-[#2a2d33] dark:bg-[#1d2128]',
                      'shadow-[1px_1px_50px_1px_rgba(55,61,67,0.11)]'
                    )}
                    role="presentation"
                  >
                    <div
                      role="listbox"
                      className={clsx(
                        'box-border flex-1 overflow-x-hidden overflow-y-auto',
                        '[scrollbar-gutter:stable]',
                        '[&::-webkit-scrollbar]:w-[9px]',
                        '[&::-webkit-scrollbar-track]:bg-transparent',
                        '[&::-webkit-scrollbar-thumb]:min-h-[29px]',
                        '[&::-webkit-scrollbar-thumb]:rounded-[7.5px]',
                        '[&::-webkit-scrollbar-thumb]:bg-[#E9ECF0]',
                        'dark:[&::-webkit-scrollbar-thumb]:bg-[#2a2d33]'
                      )}
                      style={{
                        paddingTop: PANEL_PAD_Y,
                        paddingBottom: PANEL_PAD_Y,
                        paddingLeft: PANEL_PAD_LEFT,
                        paddingRight: PANEL_PAD_RIGHT,
                      }}
                    >
                      {isLoading ? (
                        <p className="py-12 text-center text-[16px] text-[#989898] dark:text-[#7d8794]">
                          {trans(message('Loading...'))}
                        </p>
                      ) : filtered.length === 0 ? (
                        <p className="py-12 text-center text-[16px] text-[#989898] dark:text-[#7d8794]">
                          {trans(
                            message('There are no items matching your query')
                          )}
                        </p>
                      ) : (
                        <div className="flex flex-col items-center">
                          {filtered.map((country, index) => (
                            <button
                              key={country.code}
                              type="button"
                              role="option"
                              aria-selected={
                                field.value === country.code ? 'true' : 'false'
                              }
                              className={countryRowClass}
                              style={{
                                width: ROW_WIDTH,
                                height: ROW_HEIGHT,
                                marginBottom:
                                  index < filtered.length - 1 ? ROW_GAP : 0,
                              }}
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => selectCountry(country)}
                            >
                              {country.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>,
                rootEl
              )}
          </div>
        );
      }}
    />
  );
}

function mergeInputRef(
  floatingRef: (node: HTMLInputElement | null) => void,
  inputRef: MutableRefObject<HTMLInputElement | null>
) {
  return (node: HTMLInputElement | null) => {
    floatingRef(node);
    inputRef.current = node;
  };
}
